import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/futures.png';

import { Database, Zap, Globe, Clock, TrendingDown, Activity } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/futures');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/futures-positioning-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/futures-curve-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/market-breadth-vehicle-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/catalyst-radar-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const StocksPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Futures Data API – Commodities & Futures Prices"
        description="Futures data API with daily and historical futures prices for 2,000+ contracts across 25+ exchanges, plus commodities data, open interest, and curve analysis."
        canonicalUrl="https://axionquant.com/products/futures"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Futures Data API",
          "description": "Futures data API with daily and historical futures prices for 2,000+ contracts across 25+ exchanges, plus commodities data, open interest, and curve analysis.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Futures Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>


      <Header
  docs="/docs/01-market-data/futures"
  title="Futures Data API & Commodities"
  body="Daily and hourly futures prices across energy, metals, agriculture, and financial futures contracts, with decades of history back to 1987. Filter by exchange, inspect contract metadata, and run multi-contract spread analysis with a full technical toolkit."
  crumb={{name: 'Futures', path:'/products/futures'}}
  tabs={[
    {
      label: 'List by Exchange',
      language: 'python',
      filename: 'list_by_exchange.py',
      bg: '21.jpg',
      code: `from axion import Axion, utils

client = Axion(api_key="your_api_key")

exchanges = ["CME", "CBOT", "NYMEX"]

contracts = {
    ex: client.futures.tickers(exchange=ex)
    for ex in exchanges
}

volume_by_exchange = {
    ex: len(tickers)
    for ex, tickers in contracts.items()
}

all_tickers = utils.simmer([
    tickers for tickers in contracts.values()
])
`,
    },
    {
      label: 'Contract Metadata',
      language: 'python',
      filename: 'contract_metadata.py',
      bg: '22.jpg',
      code: `from axion import Axion, utils

client = Axion(api_key="your_api_key")

watchlist = ["ES=F", "CL=F", "GC=F", "NG=F", "ZC=F"]

def fetch_metadata(row):
    t    = row["ticker"]
    meta = client.futures.ticker(t)
    return (t, {
        "ticker":   t,
        "name":     meta["name"],
        "exchange": meta["exchange"],
    })

df_watch = utils.df([{"ticker": t} for t in watchlist])
meta_map = {}
utils.work(df_watch, fetch_metadata, meta_map)

df_meta = utils.df(list(meta_map.values()))
`,
    },
    {
      label: 'Price & Volatility',
      language: 'python',
      filename: 'price_volatility.py',
      bg: '23.jpg',
      code: `from axion import Axion, utils, visualize, ta

client = Axion(api_key="your_api_key")

df = utils.df(
    client.futures.prices(
        "CL=F",
        from_date=utils.d(utils.yearago),
        frame="daily",
    )
)

upper, mid, lower = ta.bbands(df, period=20, num_std_dev=2)
df["bb_upper"] = upper
df["bb_mid"]   = mid
df["bb_lower"] = lower
df["atr"]      = ta.atr(df, period=14)

visualize.graph(
    df,
    x="date",
    lines=["close", "bb_upper", "bb_mid", "bb_lower"],
    bars=["atr"],
    title="Crude Oil (CL=F) - Price & Volatility Regime",
)
`,
    },
    {
      label: 'Multi-Contract Spread',
      language: 'python',
      filename: 'spread_analysis.py',
      bg: '24.jpg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

pairs   = [("CL=F", "NG=F"), ("GC=F", "SI=F")]
from_dt = utils.d(utils.yearago)

for leg_a, leg_b in pairs:
    df_a = utils.df(client.futures.prices(leg_a, from_date=from_dt))
    df_b = utils.df(client.futures.prices(leg_b, from_date=from_dt))

    visualize.spread(
        [df_a, df_b],
        x="date",
        y="close",
    )
`,
    },
  ]}
/>

      <Marquee/>

      <FeaturesGrid
        title="Futures Data API. "
        subtitle="Commodities, futures prices, and market data. "
        features={ [
        {
          fig: 'FIG 0.1',
          icon: <Zap size={80} color="var(--purple)" />,
          title: 'Daily & Hourly Data',
          description: 'Daily and hourly futures prices across energy, metals, agriculture, and financials. Pull decades of history back to 1987 nearly instantly for backtesting and long-horizon research.',
        },
        {
          fig: 'FIG 0.2',
          icon: <Clock size={80} color="var(--green)" />,
          title: 'Historical Data',
          description: 'Complete futures prices history back to 1987. Daily OHLCV bars for backtesting commodity and quantitative research.',
        },
        {
          fig: 'FIG 0.3',
          icon: <Database size={80} color="var(--red)" />,
          title: 'Corporate Actions',
          description: 'Comprehensive dividend, split, merger, and spinoff data. Adjust historical prices accurately for corporate events.',
        },
        {
          fig: 'FIG 0.4',
          icon: <TrendingDown size={80} color="var(--purple)" />,
          title: 'Short Interest',
          description: 'Track short interest, days to cover, and short squeeze potential. Essential data for understanding market sentiment and positioning.',
        },
        {
          fig: 'FIG 0.5',
          icon: <Activity size={80} color="var(--green)" />,
          title: 'Insider Trading',
          description: 'Monitor insider transactions from Form 4 filings. Track buying and selling activity from executives, directors, and institutional holders.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Globe size={80} color="var(--red)" />,
          title: 'Global Coverage',
          description: 'Futures market data across 85+ exchanges and 2,000+ contracts spanning energy, metals, agriculture, indices, and rates from one API.',
        },
      ]}/>

      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Futures Data Platform"
        headingPlain="Futures data"
        headingEm="for every contract."
        body="Institutional-grade futures data API across 25+ exchanges, covering energy, metals, agriculture, indices, currencies, and rates. Normalized, timestamped, and ready for production."
        stats={[
          { v: "2,000+", l: "Contracts" },
          { v: "25+", l: "Exchanges" },
          { v: "35+ years", l: "Historical Data" },
          { v: "10+", l: "Asset Classes" },
        ]}
        markers={[
          // North America
          { id: "cme",     location: [41.8827, -87.6298], label: "CME Group"      },
          { id: "ice_us",  location: [40.7130, -74.0060], label: "ICE US"         },
          { id: "cbot",    location: [41.8827, -87.6298], label: "CBOT"           },
          { id: "cme_ny",  location: [40.7130, -74.0060], label: "NYMEX"          },
          { id: "comex",   location: [40.7130, -74.0060], label: "COMEX"          },
          { id: "kansas",  location: [39.0482, -95.6778], label: "KCBT"           },
          { id: "minnea",  location: [44.9778, -93.2650], label: "MGE"            },
          { id: "tsx",     location: [43.6510, -79.3830], label: "MX"             },
          { id: "meff",    location: [19.4326, -99.1332], label: "MexDer"         },

          // Europe
          { id: "eurex",   location: [50.1155, 8.6842],   label: "Eurex"          },
          { id: "ice_eur", location: [51.5142, -0.0931],  label: "ICE Europe"     },
          { id: "lme",     location: [51.5129, -0.0965],  label: "LME"            },
          { id: "euronext",location: [48.8566, 2.3522],   label: "Euronext"       },
          { id: "eurex_zurich", location: [47.3769, 8.5417], label: "Eurex Zurich" },
          { id: "nasdaq_omx", location: [59.3293, 18.0686], label: "Nasdaq OMX"   },
          { id: "meff_spain", location: [40.4168, -3.7038], label: "MEFF"         },
          { id: "moex",    location: [55.7558, 37.6173],  label: "MOEX"           },
          { id: "warsaw",  location: [52.2297, 21.0122],  label: "WSE"            },
          { id: "prague",  location: [50.0755, 14.4378],  label: "PSE"            },
          { id: "athens",  location: [37.9838, 23.7275],  label: "ADEX"           },

          // Asia
          { id: "jpx",     location: [35.6762, 139.7649], label: "JPX"            },
          { id: "tocom",   location: [35.6762, 139.7649], label: "TOCOM"          },
          { id: "ose",     location: [34.6938, 135.5022], label: "OSE"            },
          { id: "sgx",     location: [1.2847, 103.8508],  label: "SGX"            },
          { id: "hkex",    location: [22.3027, 114.1634], label: "HKEX"           },
          { id: "dce",     location: [38.9140, 121.6147], label: "DCE"            },
          { id: "zce",     location: [34.7466, 113.6258], label: "ZCE"            },
          { id: "cffex",   location: [31.2304, 121.4737], label: "CFFEX"          },
          { id: "sfe",     location: [31.2304, 121.4737], label: "SHFE"           },
          { id: "nse",     location: [19.0760, 72.8777],  label: "NSE"            },
          { id: "bse",     location: [19.0760, 72.8777],  label: "BSE"            },
          { id: "ice_sg",  location: [1.2847, 103.8508],  label: "ICE Singapore"  },
          { id: "bmd",     location: [3.1390, 101.6869],  label: "BMD"            },
          { id: "tfex",    location: [13.7367, 100.5231], label: "TFEX"           },
          { id: "kse",     location: [37.5665, 126.9780], label: "KRX"            },
          { id: "idr",     location: [-6.2088, 106.8456], label: "IDX"            },

          // Middle East & South Asia
          { id: "dgc",     location: [25.2048, 55.2708],  label: "DGCX"           },
          { id: "cme_me",  location: [25.2048, 55.2708],  label: "CME ME"         },
          { id: "turkdex", location: [41.0082, 28.9784],  label: "TurkDEX"        },
          { id: "isx",     location: [32.4279, 53.6880],  label: "IME"            },
          { id: "psx",     location: [24.8607, 67.0011],  label: "PSX"            },

          // Africa
          { id: "jse",     location: [-26.2041, 28.0473], label: "JSE"            },
          { id: "egex",    location: [30.0444, 31.2357],  label: "EGX"            },
          { id: "cse",     location: [-33.9249, 18.4241], label: "CSE"            },

          // South America
          { id: "b3",      location: [-23.5505, -46.6333], label: "B3"            },
          { id: "rofex",   location: [-34.6037, -58.3816], label: "Rofex"         },
          { id: "bcba",    location: [-34.6037, -58.3816], label: "BCBA"          },
          { id: "bvc",     location: [4.7110, -74.0721],  label: "BVC"            },

          // Oceania
          { id: "asx",     location: [-33.8688, 151.2093], label: "ASX"           },
          { id: "nzx",     location: [-36.8485, 174.7633], label: "NZX"           },
        ]}
      />


         <DataGrid
        title="Futures Data API: The Full Curve, Every Market"
        subtitle="Trade with conviction across global commodity, index, and rate futures. Access granular term structure, roll analytics, volume, and open interest data to model contango, backwardation, and roll yield."
        bg={databg}
        stats={[
          { label: 'Futures Contracts', value: '2,000+' },
          { label: 'Exchanges Covered', value: '25+' },
          { label: 'Historical Depth', value: '35+ years' },
        ]}
      />
      <CardList
        title="Master the Term Structure"
        vid={'https://cdn.studio14a.com/axion/products/cards/futures.mp4'}
        points={[
          {
            "title": "Complete Term Structure & Roll Analytics",
            "details": "Access continuous contracts, precise roll dates, and full historical term structure data (prices, volumes, OI) across all expiries to accurately model roll costs and curve dynamics.",
            "cta": "Explore contract rolls",
            "url": "/docs"
          },
          {
            "title": "Commodity Fundamentals & Inventories",
            "details": "Futures prices paired with commodities data and fundamental drivers. Track global inventories, production data, shipping rates, and weather impacts for energy, metals, and soft commodities.",
            "cta": "View commodity fundamentals",
            "url": "/products/economics"
          },
          {
            "title": "Volume Profile & Market Microstructure",
            "details": "Analyze intraday volume-at-price, block trade activity, and order flow imbalances. Identify liquidity pockets and institutional positioning in the futures complex.",
            "cta": "Analyze market depth",
            "url": "/docs"
          }
        ]}
      />

      <Responses
        heading='Futures Market Data'
        subheading='Complete historical and metadata for 2,000+ futures contracts across 25+ exchanges'
        ticker='ALI'
        tabs={[
          {
            title: 'All Contracts',
            description: 'List all available futures contracts with metadata',
            data: [
              { "id": 1, "ticker": "ALI", "name": "Aluminum Futures,Feb-2026", "exchange": "CMX" },
              { "id": 2, "ticker": "M6A", "name": "Micro AUD/USD Futures,Mar-2026", "exchange": "CME" },
              { "id": 3, "ticker": "BTC", "name": "Bitcoin Futures,Dec-2025", "exchange": "CME" }
            ]
          },
          {
            title: 'Contract Details',
            description: 'Metadata for a specific futures ticker',
            data: {
              "id": 1,
              "ticker": "ALI",
              "name": "Aluminum Futures,Feb-2026",
              "exchange": "CMX"
            }
          },
          {
            title: 'Daily Prices',
            description: 'Raw daily OHLCV data for a contract',
            data: [
              {
                "time": "2025-12-19T16:02:30.000Z",
                "ticker": "ALI",
                "open": "2920.2500",
                "high": "2965.2500",
                "low": "2914.0000",
                "close": "2964.0000",
                "volume": "327"
              },
              {
                "time": "2025-12-18T05:00:00.000Z",
                "ticker": "ALI",
                "open": "2823.7500",
                "high": "2823.7500",
                "low": "2823.7500",
                "close": "2823.7500",
                "volume": "0"
              }
            ]
          },
          {
            title: 'Latest Quote',
            description: 'Latest quote for a futures contract including latest price, EMAs, 52-week range, and YTD performance',
            data: {
              "latestClose": 3399,
              "latestOpen": 3550.25,
              "latestHigh": 3550.25,
              "latestLow": 3384.5,
              "previousClose": 3860.75,
              "week52Range": {
                "high": 4195,
                "low": 2427
              },
              "week52AvgClose": 2992.0369,
              "ema30": 3772.3542,
              "ema60": 3661.9218,
              "ema90": 3542.7218,
              "ema120": 3438.227,
              "ytdClosePctChange": 15.6909,
              "volume": 411,
              "avgVolumeLifetime": 7.1121
            },
          },
          {
            title: 'Market Gainers',
            description: 'Top futures gainers ranked by percentage change over the last 5 days',
            data: [
              { ticker: "ES", pctchange: 1.23 },
              { ticker: "NQ", pctchange: 0.99 },
              { ticker: "RTY", pctchange: 0.87 },
              { ticker: "YM", pctchange: 0.76 },
              { ticker: "CL", pctchange: 0.65 },
              { ticker: "GC", pctchange: 0.54 },
              { ticker: "SI", pctchange: 0.43 },
              { ticker: "HG", pctchange: 0.32 },
              { ticker: "ZW", pctchange: 0.28 },
              { ticker: "ZC", pctchange: 0.21 },
            ],
          },
          {
            title: 'Market Losers',
            description: 'Top futures losers ranked by percentage change over the last 5 days',
            data: [
              { ticker: "ES", pctchange: -1.23 },
              { ticker: "NQ", pctchange: -0.99 },
              { ticker: "RTY", pctchange: -0.87 },
              { ticker: "YM", pctchange: -0.76 },
              { ticker: "CL", pctchange: -0.65 },
              { ticker: "GC", pctchange: -0.54 },
              { ticker: "SI", pctchange: -0.43 },
              { ticker: "HG", pctchange: -0.32 },
              { ticker: "ZW", pctchange: -0.28 },
              { ticker: "ZC", pctchange: -0.21 },
            ],
          },
        ]}
      />

      <DatasetDetails full={true}
        title="Comprehensive Futures Data Coverage"
        subtitle="Access futures market data and commodities data with historical prices dating back to 1987 for 2,000+ contracts across major global exchanges. Power quantitative trading strategies, risk management systems, and portfolio analytics with institutional-grade data feeds."
        cta="Explore Now"
        ctaLink="/login"
        tiles={[
          {
            img: '10.webp',
            title: 'We cover 2,000+ futures contracts',
            caption: 'You can access futures prices and derivatives data across all major global exchanges.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '4.webp',
            title: 'We connect to 85+ major exchanges',
            caption: 'You get comprehensive coverage of global derivatives trading venues with daily and hourly data.',
            cn: 'scaled corner',
            tile: <W2/>
          },
          {
            img: '2.webp',
            title: 'We provide 400B+ data points',
            caption: 'You can analyze futures markets with an extensive dataset spanning decades.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '5.webp',
            title: 'We offer 35+ years of history',
            caption: 'You can backtest commodity and futures strategies across complete market cycles.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '12.webp',
            title: 'We retrieve data in milliseconds',
            caption: 'You can pull decades of history nearly instantly to power long-horizon research and backtesting.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Futures data API coverage, without compromise."
        leftCell={{
          stat: '2,000+',
          title: '2,000+ contracts covered.',
          desc: 'Every energy, metal, ag, index, rate, and FX future - across 25+ exchanges.',
          showChart: true,
        }}
        rightCells={[
          {
            stat: '35+yr',
            title: '35+ Years of History.',
            desc: 'Daily and hourly prices back to 1987, retrievable nearly instantly.',
          },
          {
            stat: '99.95%',
            title: '99.95% uptime SLA.',
            desc: 'Reliable data for mission-critical research and trading operations.',
            boldDesc: true,
          },
        ]}
      /><ProductFAQ slug="futures" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default StocksPage;

